import { useState, useRef, useEffect, useCallback } from "react";
import { chatRooms, quickEmojis } from "@/mocks/liveChat";
import type { ChatMessage, ChatChannel } from "@/mocks/liveChat";
import { useBlockedUsers } from "@/hooks/useBlockedUsers";
import Swal from "sweetalert2";

const REPORTS_KEY = 'rfw_app_chat_reports';

export default function AppCommunityChat() {
  const [activeRoomId, setActiveRoomId] = useState(chatRooms[0]?.id || "");
  const [activeChannelId, setActiveChannelId] = useState("");
  const [inputText, setInputText] = useState("");
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [localMessages, setLocalMessages] = useState<Record<string, ChatMessage[]>>({});
  const [mutedUsers, setMutedUsers] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { blockUser, isBlocked } = useBlockedUsers();

  const activeRoom = chatRooms.find((r) => r.id === activeRoomId);
  const activeChannel = activeRoom?.channels.find((c) => c.id === activeChannelId);
  const messages = activeChannelId
    ? (localMessages[activeChannelId] || activeRoom?.channels.find((c) => c.id === activeChannelId)?.messages || [])
    : [];

  useEffect(() => {
    if (activeRoom && !activeChannelId) {
      const firstWithMessages = activeRoom.channels.find((c) => c.messages.length > 0);
      setActiveChannelId(firstWithMessages?.id || activeRoom.channels[0]?.id || "");
    }
  }, [activeRoomId, activeRoom]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const simulatedUserId = "u-me";
  const simulatedUserName = "Marco Rivera";

  const handleSend = useCallback(() => {
    const text = inputText.trim();
    if (!text || !activeChannelId) return;

    const newMsg: ChatMessage = {
      id: Date.now(),
      userId: simulatedUserId,
      userName: simulatedUserName,
      userAvatar: "https://readdy.ai/api/search-image?query=Young%20Hispanic%20male%20portrait%20friendly%20warm%20smile%20casual%20modern%20style%20clean%20background%20soft%20natural%20lighting&width=80&height=80&seq=chat-user-marco&orientation=squarish",
      userTier: "Top Fan",
      userTierColor: "text-orange-500",
      content: replyTo ? `@${replyTo.userName} ${text}` : text,
      timestamp: new Date().toISOString(),
      isModerator: false,
      isPinned: false,
      reactions: [],
    };

    setLocalMessages((prev) => ({
      ...prev,
      [activeChannelId]: [...(prev[activeChannelId] || messages), newMsg],
    }));
    setInputText("");
    setReplyTo(null);
  }, [inputText, activeChannelId, replyTo, messages]);

  const handleReaction = useCallback(
    (msgId: number, emoji: string) => {
      if (!activeChannelId) return;
      setLocalMessages((prev) => {
        const msgs = [...(prev[activeChannelId] || messages)];
        return {
          ...prev,
          [activeChannelId]: msgs.map((m) => {
            if (m.id !== msgId) return m;
            const existing = m.reactions.find((r) => r.emoji === emoji);
            if (existing) {
              const newReactions = existing.reacted
                ? m.reactions.map((r) =>
                    r.emoji === emoji ? { ...r, count: r.count - 1, reacted: false } : r
                  )
                : m.reactions.map((r) =>
                    r.emoji === emoji ? { ...r, count: r.count + 1, reacted: true } : r
                  );
              return { ...m, reactions: newReactions.filter((r) => r.count > 0) };
            }
            return { ...m, reactions: [...m.reactions, { emoji, count: 1, reacted: true }] };
          }),
        };
      });
    },
    [activeChannelId, messages]
  );

  const handleReport = (msg: ChatMessage) => {
    Swal.fire({
      title: 'Reportar mensaje',
      text: `¿Querés reportar el mensaje de ${msg.userName}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, reportar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#d1d5db',
      customClass: { popup: 'rounded-2xl', title: 'font-bold', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap', cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap' },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        const reports = JSON.parse(localStorage.getItem(REPORTS_KEY) || '[]');
        reports.push({ id: Date.now(), username: msg.userName, content: msg.content, timestamp: new Date().toISOString() });
        localStorage.setItem(REPORTS_KEY, JSON.stringify(reports));
        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: `Mensaje de ${msg.userName} reportado`, showConfirmButton: false, timer: 2000, timerProgressBar: true });
      }
    });
  };

  const handleBlock = (msg: ChatMessage) => {
    Swal.fire({
      title: 'Bloquear usuario',
      text: `¿Querés bloquear a ${msg.userName}? No podrá enviarte mensajes ni interactuar con vos.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, bloquear',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#d1d5db',
      customClass: { popup: 'rounded-2xl', title: 'font-bold', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap', cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap' },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        blockUser(msg.userName, msg.userAvatar, 'Bloqueado desde el chat');
        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: `${msg.userName} bloqueado`, showConfirmButton: false, timer: 2000, timerProgressBar: true });
      }
    });
  };

  const handleMute = (msg: ChatMessage) => {
    Swal.fire({
      title: 'Silenciar usuario',
      text: `¿Querés silenciar a ${msg.userName} por 5 minutos?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, silenciar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#d1d5db',
      customClass: { popup: 'rounded-2xl', title: 'font-bold', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap', cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap' },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setMutedUsers((prev) => new Set(prev).add(msg.userName));
        setTimeout(() => {
          setMutedUsers((prev) => {
            const next = new Set(prev);
            next.delete(msg.userName);
            return next;
          });
        }, 5 * 60 * 1000);
        Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: `${msg.userName} silenciado por 5 min`, showConfirmButton: false, timer: 2000, timerProgressBar: true });
      }
    });
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  };

  const activeRoom2 = chatRooms.find((r) => r.id === activeRoomId);

  return (
    <div className="h-full flex flex-col bg-background-50">
      {/* Room selector */}
      <div className="flex overflow-x-auto gap-1.5 px-3 py-2 scrollbar-none border-b border-background-200 shrink-0">
        {chatRooms.map((room) => (
          <button
            key={room.id}
            onClick={() => { setActiveRoomId(room.id); setActiveChannelId(""); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
              activeRoomId === room.id
                ? "bg-foreground-950 text-background-50"
                : "bg-background-100 text-foreground-500 hover:bg-background-200"
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${room.iconBg}`}></span>
            {room.name}
          </button>
        ))}
      </div>

      {/* Channel tabs */}
      {activeRoom2 && activeRoom2.channels.length > 0 && (
        <div className="flex overflow-x-auto gap-1 px-3 py-1.5 scrollbar-none border-b border-background-100 shrink-0">
          {activeRoom2.channels.map((ch) => (
            <button
              key={ch.id}
              onClick={() => setActiveChannelId(ch.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap transition-all cursor-pointer relative ${
                activeChannelId === ch.id
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-500 hover:bg-background-200"
              }`}
            >
              #{ch.name}
              {ch.unreadCount > 0 && (
                <span className="w-4 h-4 rounded-full bg-rose-500 text-white text-[8px] font-bold flex items-center justify-center">
                  {ch.unreadCount > 99 ? "99+" : ch.unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Channel info */}
      {activeChannel && (
        <div className="px-3 py-1.5 bg-background-50 border-b border-background-100 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-xs font-bold text-foreground-900 truncate">#{activeChannel.name}</span>
              <span className="flex items-center gap-1 text-[10px] text-emerald-600">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                {activeChannel.onlineCount} online
              </span>
            </div>
            {activeChannel.slowMode && (
              <span className="text-[9px] text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                Slow {activeChannel.slowModeSeconds}s
              </span>
            )}
          </div>
          {activeChannel.description && (
            <p className="text-[10px] text-foreground-500 mt-0.5 line-clamp-1">{activeChannel.description}</p>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-3 scrollbar-none">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-foreground-400 gap-2">
            <i className="ri-chat-3-line text-3xl"></i>
            <p className="text-xs">No hay mensajes todavía</p>
            <p className="text-[10px]">¡Sé el primero en escribir!</p>
          </div>
        )}

        {messages.map((msg) => {
          const isMe = msg.userId === simulatedUserId;
          return (
            <div key={msg.id} className={`flex gap-2 ${isMe ? "flex-row-reverse" : ""}`}>
              {/* Avatar */}
              <div className="w-8 h-8 rounded-full overflow-hidden bg-background-200 shrink-0">
                <img src={msg.userAvatar} alt={msg.userName} className="w-full h-full object-cover object-top" loading="lazy" />
              </div>

              <div className={`min-w-0 flex-1 ${isMe ? "flex flex-col items-end" : ""}`}>
                {/* Header */}
                <div className={`flex items-center gap-1.5 mb-0.5 ${isMe ? "flex-row-reverse" : ""}`}>
                  <span className="text-[11px] font-bold text-foreground-900">{msg.userName}</span>
                  {msg.userBadge && <span className="text-[10px]">{msg.userBadge}</span>}
                  <span className={`text-[9px] font-semibold ${msg.userTierColor}`}>{msg.userTier}</span>
                  {msg.isModerator && (
                    <span className="w-4 h-4 rounded-full bg-rose-100 flex items-center justify-center">
                      <i className="ri-shield-check-line text-rose-500 text-[8px]"></i>
                    </span>
                  )}
                  <span className="text-[9px] text-foreground-400">{formatTime(msg.timestamp)}</span>
                </div>

                {/* Message bubble */}
                <div className={`group relative inline-block max-w-full`}>
                  {msg.isPinned && (
                    <div className="flex items-center gap-1 mb-0.5">
                      <i className="ri-pushpin-fill text-amber-500 text-[10px]"></i>
                      <span className="text-[9px] text-amber-600 font-semibold">Fijado</span>
                    </div>
                  )}
                  <div
                    className={`rounded-2xl px-3 py-2 text-xs leading-relaxed ${
                      isMe
                        ? "bg-primary-500 text-background-50 rounded-tr-md"
                        : "bg-background-100 text-foreground-900 rounded-tl-md"
                    }`}
                  >
                    {msg.content}
                  </div>

                  {/* Reactions */}
                  {msg.reactions.length > 0 && (
                    <div className={`flex flex-wrap gap-1 mt-1 ${isMe ? "justify-end" : ""}`}>
                      {msg.reactions.map((r) => (
                        <button
                          key={r.emoji}
                          onClick={() => handleReaction(msg.id, r.emoji)}
                          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] transition-all cursor-pointer ${
                            r.reacted
                              ? "bg-primary-100 text-primary-700 border border-primary-300"
                              : "bg-background-100 text-foreground-500 border border-background-200 hover:border-foreground-300"
                          }`}
                        >
                          {r.emoji} {r.count}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Actions (on hover/tap) */}
                  <div className={`absolute ${isMe ? "-left-20" : "-right-20"} top-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5`}>
                    <button
                      onClick={() => handleReaction(msg.id, "❤️")}
                      className="w-6 h-6 rounded-full bg-background-100 hover:bg-rose-100 flex items-center justify-center cursor-pointer transition-all"
                      title="Me gusta"
                    >
                      <i className="ri-heart-line text-foreground-500 text-[10px]"></i>
                    </button>
                    <button
                      onClick={() => setReplyTo(msg)}
                      className="w-6 h-6 rounded-full bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer transition-all"
                      title="Responder"
                    >
                      <i className="ri-reply-line text-foreground-500 text-[10px]"></i>
                    </button>
                    <button
                      onClick={() => handleReport(msg)}
                      className="w-6 h-6 rounded-full bg-background-100 hover:bg-red-100 flex items-center justify-center cursor-pointer transition-all"
                      title="Reportar"
                    >
                      <i className="ri-alert-line text-foreground-500 text-[10px]"></i>
                    </button>
                    <button
                      onClick={() => handleBlock(msg)}
                      className="w-6 h-6 rounded-full bg-background-100 hover:bg-red-100 flex items-center justify-center cursor-pointer transition-all"
                      title="Bloquear"
                    >
                      <i className="ri-forbid-line text-foreground-500 text-[10px]"></i>
                    </button>
                    <button
                      onClick={() => handleMute(msg)}
                      className="w-6 h-6 rounded-full bg-background-100 hover:bg-amber-100 flex items-center justify-center cursor-pointer transition-all"
                      title="Silenciar"
                    >
                      <i className="ri-volume-mute-line text-foreground-500 text-[10px]"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply indicator */}
      {replyTo && (
        <div className="px-3 py-1.5 bg-background-100 border-t border-background-200 flex items-center gap-2 shrink-0">
          <i className="ri-reply-line text-foreground-500 text-xs"></i>
          <span className="text-[10px] text-foreground-600 flex-1 truncate">
            Respondiendo a <strong>{replyTo.userName}</strong>: {replyTo.content.slice(0, 40)}...
          </span>
          <button onClick={() => setReplyTo(null)} className="w-5 h-5 rounded-full bg-foreground-200 flex items-center justify-center cursor-pointer">
            <i className="ri-close-line text-foreground-600 text-[10px]"></i>
          </button>
        </div>
      )}

      {/* Emoji picker */}
      {showEmojiPicker && (
        <div className="px-3 py-1.5 bg-background-50 border-t border-background-200 flex gap-1 flex-wrap shrink-0">
          {quickEmojis.map((emoji) => (
            <button
              key={emoji}
              onClick={() => { setInputText((prev) => prev + emoji); setShowEmojiPicker(false); inputRef.current?.focus(); }}
              className="w-8 h-8 rounded-lg hover:bg-background-200 flex items-center justify-center text-sm cursor-pointer transition-all"
            >
              {emoji}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      {activeChannel && (
        <div className="p-2 border-t border-background-200 bg-background-50 shrink-0 safe-bottom">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="w-9 h-9 rounded-xl bg-background-100 hover:bg-background-200 flex items-center justify-center cursor-pointer transition-all shrink-0"
            >
              <i className={`${showEmojiPicker ? "ri-keyboard-line" : "ri-emotion-line"} text-foreground-500 text-sm`}></i>
            </button>
            <input
              ref={inputRef}
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder={activeChannel.slowMode ? `Mensaje (espera ${activeChannel.slowModeSeconds}s)...` : "Escribí un mensaje..."}
              className="flex-1 bg-background-100 border border-background-200 rounded-xl px-3 py-2 text-xs text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400"
            />
            <button
              onClick={handleSend}
              disabled={!inputText.trim()}
              className="w-9 h-9 rounded-xl bg-primary-500 hover:bg-primary-600 disabled:opacity-40 flex items-center justify-center cursor-pointer transition-all shrink-0"
            >
              <i className="ri-send-plane-fill text-background-50 text-sm"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}